import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlexContainer } from '../../../../common/flex-container/flex-container.styles';
import {
  HeaderSecondary,
  TextBody,
} from '../../../../common/typography/typography.styles';
import {
  DeliveryStatusHeader,
  DeliveryStatusMore,
  DeliveryStatusAccordionTrigger,
  DeliveryStatuses,
  DeliveryStatusItem,
  Section,
  TrackingCodeButton,
  CodeGenerationButtonsContainer,
  CodeStringWrapper,
  CodeGenerationContainer,
} from '../details-sidebar.styles';
import { ReactComponent as ChevronDownIcon } from '../../../../assets/chevron-down.svg';
import { ReactComponent as NoLabelIcon } from '../../../../assets/delivery-tag-icons/printer-crossed-current-color.svg';
import { ReactComponent as LabelRequiredIcon } from '../../../../assets/delivery-tag-icons/printer.svg';
import MoreDropdown from '../../more-dropdown/more-dropdown.component';
import MoreSidebar from '../../more-sidebar/more-sidebar.component';
import {
  ICardActionItem,
  getStatusBadgeConfig,
} from '../../../../routes/parcel-list/config';
import { Shipment } from '../../../../models/parcelDeliveryMiles';
import {
  copyToClipboard,
  formatUtcDateTime,
} from '../../../../utils/generic/generic.utils';

import Button from '../../../../common/button/button.component';
import { useModal } from '../../../../common/modal';
import { Modals } from '../../../../constants/modals';
import { MarginContainer } from '../../../../common/base-margin/base-margin.component';
import Sidebar from '../../../../common/sidebar-right/sidebar.component';
import { useStore } from '../../../../stores/store';
import ParcelHistory from './parcel-history/parcel-history.component';
import { SidebarName } from '../../../../types/sidebar.types';
import { client } from '../../../../config';
import { isMeest } from '../../../../utils/client/client.utils';
import PinnedLabelComponent from '../../pinned-label/pinned-label.component';
import { ReactComponent as CopyIcon } from '../../../../assets/copy-icon.svg';
import { ReactComponent as ClockIcon } from '../../../../assets/delivery-time-clock.svg';
import Tip from '../../../../common/toasts/tip.component';
import { DeliveryStatusContent, DeliveryTimePanel } from './styles';
import { useIsMobile } from '../../../../hooks/use-is-mobile.hook';

interface Props {
  menuItems: ICardActionItem[];
  shipment: Shipment;
  isGuestUser?: boolean;
}

const sidebarFullHistory: SidebarName = 'full_history';

const DeliveryStatus = ({
  menuItems,
  shipment,
  isGuestUser = false,
}: Props) => {
  const {
    id: shipmentId,
    is_favorite: isFavorite,
    has_barcode: hasBarcode,
    barcode,
    tracking_number: trackingNumber,
    status,
    status_history: statusHistory,
    delivery_time_title: deliveryTimeTitle,
    flags,
  } = shipment;

  const isMobile = useIsMobile();
  const needLabel = !!flags?.need_label;

  const { t } = useTranslation();
  const { openModal } = useModal();

  const {
    navStore: { toggleSidebarByName },
  } = useStore();

  const statusId = useId();
  const [isDeliveryStatusOpen, setIsDeliveryStatusOpen] = useState(false);

  const handleButtonClick = () => {
    openModal({
      id: Modals.CODE_GENERATION,
      name: Modals.CODE_GENERATION,
      props: {
        code: barcode,
        shipmentId,
      },
    });
  };

  const statusConfig = getStatusBadgeConfig(status);

  const shouldShowCodeGenerationButtons = !!barcode;
  const deliveryTimeText = [
    deliveryTimeTitle?.delivery_time_title?.trim(),
    deliveryTimeTitle?.delivery_time_details?.trim(),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Section className='delivery-status-panel'>
      <DeliveryStatusHeader>
        <FlexContainer
          $gap='4px'
          $align='center'>
          <TrackingCodeButton
            type='button'
            onClick={() =>
              copyToClipboard({
                text: trackingNumber ?? '',
                successMessage: t('parcel_number_is_copied'),
              })
            }>
            <HeaderSecondary $size='mediumBig'>
              {trackingNumber}
            </HeaderSecondary>
            <CopyIcon />
          </TrackingCodeButton>
        </FlexContainer>

        {!isGuestUser && (
          <FlexContainer>
            {isFavorite && <PinnedLabelComponent />}
            <DeliveryStatusMore>
              <MoreDropdown
                items={menuItems}
                isInPortal={false}
              />
              <MoreSidebar
                id={shipmentId}
                items={menuItems}
              />
            </DeliveryStatusMore>
          </FlexContainer>
        )}
      </DeliveryStatusHeader>
      {deliveryTimeText && (
        <DeliveryTimePanel>
          <div className='delivery-time'>
            <ClockIcon aria-hidden='true' />
            <p>{deliveryTimeText}</p>
          </div>
          <div className={`tip-no-label ${!needLabel ? 'green' : 'red'}`}>
            {!needLabel ? <NoLabelIcon /> : <LabelRequiredIcon />}
            <p>
              {!needLabel ? t('parcel_details_no_label') : t('print_label')}
            </p>
          </div>
        </DeliveryTimePanel>
      )}
      <FlexContainer>
        <DeliveryStatusAccordionTrigger
          type='button'
          id={`${statusId}-trigger`}
          aria-controls={`${statusId}-statuses`}
          disabled={status === 'draft'}
          aria-expanded={isDeliveryStatusOpen}
          $isActive={isDeliveryStatusOpen}
          $statusColor={statusConfig.color}
          onClick={() => {
            status !== 'draft' && setIsDeliveryStatusOpen((prev) => !prev);
          }}>
          <span className='status-labels'>
            <span className='status-caption'>{t('delivery_status')}</span>
            <span className='status-value'>{t(statusConfig.text)}</span>
          </span>
          {status !== 'draft' && <ChevronDownIcon />}
        </DeliveryStatusAccordionTrigger>
      </FlexContainer>
      <DeliveryStatusContent
        id={`${statusId}-statuses`}
        aria-labelledby={`${statusId}-trigger`}
        aria-hidden={!isDeliveryStatusOpen}
        data-expanded={isDeliveryStatusOpen}
        {...(!isDeliveryStatusOpen ? { inert: '' } : {})}>
        <div className='status-content-inner'>
          <DeliveryStatuses
            $align='stretch'
            $direction='column'
            $gap='8px'>
            {statusHistory?.map((item) => {
              const isActive = status === item.status;
              const fontWeight = isActive ? 500 : 400;
              return (
                item.created_at && (
                  <DeliveryStatusItem
                    key={item.status}
                    $isActive={isActive}
                    $gap='4px'
                    $justify='space-between'>
                    <TextBody
                      $fontWeight={fontWeight}
                      $color='currentColor'>
                      {t(getStatusBadgeConfig(item.status).text)}
                    </TextBody>{' '}
                    <TextBody
                      $fontWeight={fontWeight}
                      $color='currentColor'
                      as='time'>
                      {item.status !== 'ready_to_pick_up'
                        ? formatUtcDateTime(item.created_at, true)
                        : item.created_at}
                    </TextBody>
                  </DeliveryStatusItem>
                )
              );
            })}
          </DeliveryStatuses>
          <MarginContainer
            $mt='1.2rem'
            className='full-history'>
            <Button
              appearance={isMeest(client) ? 'link' : 'tertiary'}
              contentStyle={isMeest(client) ? 'thin' : 'normal'}
              size='small'
              onClick={() => toggleSidebarByName(sidebarFullHistory)}>
              {t('parcel_details_full_history')}
            </Button>
          </MarginContainer>
        </div>
      </DeliveryStatusContent>
      {shouldShowCodeGenerationButtons && (
        <CodeGenerationContainer>
          <p>{t('parcel_details_scan_code')}</p>
          <CodeGenerationButtonsContainer>
            <CodeStringWrapper
              as='button'
              type='button'
              onClick={() =>
                copyToClipboard({
                  text: barcode ?? '',
                  successMessage: t('parcel_number_is_copied'),
                })
              }>
              <p>{barcode}</p>
              <CopyIcon />
            </CodeStringWrapper>
            <Button
              size='large'
              icon={{ glyph: 'code' }}
              disabled={!hasBarcode}
              onClick={() => handleButtonClick()}
              appearance='default'>
              {t('Code')}
            </Button>
          </CodeGenerationButtonsContainer>
        </CodeGenerationContainer>
      )}
      {!isMobile &&
        shipment.markers?.tip_v2?.message &&
        shipment.markers.tip_v2.type && (
          <MarginContainer $mt='16px'>
            <Tip
              appearance={shipment.markers.tip_v2.type}
              description={shipment.markers.tip_v2.message}
            />
          </MarginContainer>
        )}
      <Sidebar
        name={sidebarFullHistory}
        sidebarWidth='64rem'
        withoutHeader
        contentWithVerticalScroll>
        <Button
          fullWidth={false}
          icon={{ glyph: 'chevron-left' }}
          $pl='0'
          $mb='3.2rem'
          $mbMobile='2.4rem'
          size='large'
          appearance={isMeest(client) ? 'text' : 'tertiary'}
          onClick={(e) => {
            e.stopPropagation();
            toggleSidebarByName(sidebarFullHistory);
          }}>
          {t('back')}
        </Button>
        <ParcelHistory
          currentStatus={status}
          statusHistory={statusHistory}
        />
      </Sidebar>
    </Section>
  );
};

export default DeliveryStatus;
